import type { ReactNode } from 'react';
import { memo, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useLang, useMeta, useTitle } from 'hoofd';
import { clsx } from 'clsx';
import useStoreAndLanguageParams from '../hooks/useStoreAndLanguageParams';
import useCurrentStore from '../hooks/useCurrentStore';
import MaintenanceBanner from '../../main/components/MaintenanceMode/MaintenanceBanner';
import useStoreInformation from '../../content/queries/useStoreInformation';
import { isV1Header, isV2Header } from '../../main/util/getHeaderVersion';
import styles from './Layout.module.scss';
import Footer from './footer/StoreFooter';
import ScrollToTop from './ScrollToTop';
import withErrorBoundary from './errors/withErrorBoundary';
import AppHeader from './AppHeader';
import MetaLinkProvider from './providers/MetaLinkProvider';

interface LayoutProps {
    children: ReactNode;
}

export const Layout = withErrorBoundary(
    memo(({ children }: LayoutProps) => {
        const { language } = useStoreAndLanguageParams();

        const { companyName: title } = useCurrentStore();
        const { data: storeData } = useStoreInformation();
        const location = useLocation();

        const { pageTitle, description } = useMemo(() => {
            const seoConfig = storeData?.seo_config?.find(
                config => location.pathname.indexOf(config.page) !== -1
            );
            if (!seoConfig) {
                return {
                    pageTitle: title,
                    description: title,
                };
            }
            return {
                pageTitle: seoConfig.meta_fields.title,
                description: seoConfig.meta_fields.description,
            };
        }, [location.pathname, storeData?.seo_config, title]);

        const arePreOrderRoutes = window.location.href.includes('preorder');

        const headerRef = useRef<HTMLDivElement>(null);

        useTitle(pageTitle);
        useLang(language);
        useMeta({ name: 'description', content: description });

        return (
            <MetaLinkProvider>
                <div
                    className={clsx(styles.layout, {
                        [styles.headerV2]: isV2Header(),
                        [styles.headerV1]: isV1Header(),
                    })}
                >
                    <AppHeader />
                    <MaintenanceBanner />
                    <main className={styles.mainContent}>{children}</main>
                    <Footer />
                </div>
                {arePreOrderRoutes && <ScrollToTop headerRef={headerRef} />}
            </MetaLinkProvider>
        );
    })
);
